import React from 'react';
import { Alert, Divider, Flex, Typography, theme } from 'antd';
import { CaretDownFilled, InfoCircleFilled } from '@ant-design/icons';

import { ArrowLink } from '../../shared';
import type { DocsLink, ResolvedFixTip } from './diagnosticsFixTips';
import { REPORT_NOTE_MAX_TEXT_WIDTH } from './reportNoteTextWidth';

type CheckRowFixPanelProps = {
  tip: ResolvedFixTip;
  expanded: boolean;
  onToggle: () => void;
  onDocsLinkClick?: (link: DocsLink) => void;
};

export function CheckRowFixToggle({ expanded, onToggle }: Pick<CheckRowFixPanelProps, 'expanded' | 'onToggle'>) {
  const { token } = theme.useToken();

  return (
    <Typography.Link
      onClick={onToggle}
      style={{ color: token.colorPrimary, fontSize: token.fontSizeSM, whiteSpace: 'nowrap' }}
    >
      {expanded ? 'Hide fix details' : 'See how to fix this'}
      <CaretDownFilled
        style={{
          marginLeft: token.marginXXS,
          fontSize: 10,
          transition: 'transform 0.2s',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      />
    </Typography.Link>
  );
}

// Renders `backtick`-delimited spans as inline code, everything else as plain text.
function InlineFormattedText({ text }: { text: string }) {
  const segments = text.split('`');

  return (
    <>
      {segments.map((segment, i) =>
        i % 2 === 1 ? (
          <Typography.Text key={i} code>
            {segment}
          </Typography.Text>
        ) : (
          <React.Fragment key={i}>{segment}</React.Fragment>
        ),
      )}
    </>
  );
}

export default function CheckRowFixPanel({ tip, expanded, onDocsLinkClick }: Omit<CheckRowFixPanelProps, 'onToggle'>) {
  const { token } = theme.useToken();

  if (!expanded) return null;

  return (
    <>
      <Divider style={{ margin: `${token.marginXS}px 0` }} />
      <Flex vertical gap={token.marginSM} style={{ padding: `0 0 ${token.paddingXS}px` }}>
        <Typography.Text>
          <InlineFormattedText text={tip.description} />
        </Typography.Text>
        <div>
          <Typography.Text strong>Steps to fix</Typography.Text>
          <ol style={{ margin: `${token.marginXS}px 0 0`, paddingLeft: token.paddingMD }}>
            {tip.steps.map((step, i) => (
              <li key={i} style={{ marginBottom: token.marginXXS }}>
                <Typography.Text>
                  <InlineFormattedText text={step.text} />
                </Typography.Text>
                {step.codeBlock && (
                  <pre
                    style={{
                      margin: `${token.marginXS}px 0 0`,
                      padding: token.paddingSM,
                      backgroundColor: token.colorBgLayout,
                      border: `${token.lineWidth}px solid ${token.colorBorderSecondary}`,
                      borderRadius: token.borderRadiusSM,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      breakInside: 'avoid',
                      fontFamily: token.fontFamilyCode,
                      fontSize: token.fontSizeSM,
                    }}
                  >
                    {step.codeBlock}
                  </pre>
                )}
              </li>
            ))}
          </ol>
          {tip.docsLinks && tip.docsLinks.length > 0 && (
            <Flex gap={token.marginSM} style={{ marginTop: token.marginXS }} wrap="wrap">
              {tip.docsLinks.map((link) => (
                <ArrowLink
                  key={link.url}
                  href={link.url}
                  label={link.label}
                  fontSize={token.fontSizeSM}
                  onClick={() => onDocsLinkClick?.(link)}
                />
              ))}
            </Flex>
          )}
        </div>
        {tip.notice && (
          <Alert
            type="info"
            showIcon
            // Alert centers its icon against the whole message; the design puts it on the
            // first line. `alignItems` top-aligns it, and the icon's own marginTop adds the
            // half-leading the text has and the icon doesn't — without it the icon sits
            // ~3px above the first line's optical center. No `iconAlign` prop exists in
            // antd 5.29.
            style={{ fontSize: token.fontSizeSM, alignItems: 'flex-start' }}
            icon={<InfoCircleFilled style={{ marginTop: token.marginXXS }} />}
            message={
              <span style={{ display: 'block', maxWidth: REPORT_NOTE_MAX_TEXT_WIDTH }}>{tip.notice}</span>
            }
          />
        )}
      </Flex>
    </>
  );
}
